/**
 * ComparacionModal — resultado de comparar un documento adjunto (pedido OP o
 * despacho) contra los productos del pedido de Finnegans vinculado.
 *
 * Para el OP: lo importante es que todo lo del pedido esté en el OP con la
 * misma cantidad; los renglones del OP que no están en el pedido suelen ser
 * de terceros (vidrios, telas, herrajes) y se muestran como informativos.
 * Para el despacho: debe coincidir exacto en ambas direcciones.
 */

export default function ComparacionModal({ tipo, resultado, onClose }) {
  const { coinciden, cantidadDistinta, soloDocumento, soloPedido, terceros = [], grupos = [], telas = null } = resultado;
  const nombreDoc = tipo === "op" ? "pedido OP" : "despacho";

  const hayDiferencias =
    cantidadDistinta.length > 0 ||
    soloPedido.length > 0 ||
    soloDocumento.length > 0 ||
    grupos.some(g => !g.enPedido || !g.cantidadOk);

  const rowStyle = { fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid #f1f5f9", color: "#334155" };
  const seccionTitulo = (color) => ({ fontSize: 13, fontWeight: 700, color, margin: "12px 0 4px" });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 285, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 18, width: "100%",
          maxWidth: 560, maxHeight: "85vh", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>
            🔎 {tipo === "op" ? "Pedido OP" : "Despacho"} vs pedido de Finnegans
          </h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>

        <div style={{
          padding: "9px 12px", borderRadius: 8, fontSize: 13.5, fontWeight: 600,
          background: hayDiferencias ? "#fff1f2" : "#f0fdf4",
          color: hayDiferencias ? "#b91c1c" : "#15803d",
        }}>
          {hayDiferencias
            ? `⚠️ Hay diferencias entre el ${nombreDoc} y el pedido — revisar abajo`
            : `✅ Los productos del pedido coinciden con el ${nombreDoc}`}
        </div>

        <div style={{ overflowY: "auto", flex: 1, marginTop: 4 }}>
          {cantidadDistinta.length > 0 && (
            <>
              <div style={seccionTitulo("#b91c1c")}>⚠️ Cantidades distintas ({cantidadDistinta.length})</div>
              {cantidadDistinta.map((f, i) => (
                <div key={i} style={rowStyle}>
                  <b>{f.clave}</b> — {f.descPedido || f.detalle}
                  <div style={{ color: "#b91c1c" }}>
                    {nombreDoc}: {f.cantDoc} · pedido: {f.cantPedido}
                  </div>
                </div>
              ))}
            </>
          )}

          {soloPedido.length > 0 && (
            <>
              <div style={seccionTitulo("#b91c1c")}>
                ❌ En el pedido pero no en el {nombreDoc} ({soloPedido.length})
              </div>
              {soloPedido.map((p, i) => (
                <div key={i} style={rowStyle}>
                  <b>{p.cod}</b> — {p.desc} · cant. {p.cant}
                </div>
              ))}
            </>
          )}

          {soloDocumento.length > 0 && (
            <>
              <div style={seccionTitulo("#b91c1c")}>
                ❌ En el {nombreDoc} pero no en el pedido ({soloDocumento.length})
              </div>
              {soloDocumento.map((it, i) => (
                <div key={i} style={rowStyle}>
                  <b>{it.clave || it.codNum || it.alias}</b> — {it.detalle} · cant. {it.cant}
                </div>
              ))}
            </>
          )}

          {grupos.map(g => {
            const ok = g.enPedido && g.cantidadOk;
            return (
              <div key={g.key}>
                <div style={seccionTitulo(ok ? "#15803d" : "#b91c1c")}>
                  {ok ? "✅" : "❌"} {g.label} → "{g.prodNombre}" (cantidad total: {g.cantDoc})
                </div>
                <div style={{ fontSize: 12, color: ok ? "#475569" : "#b91c1c", marginBottom: 4 }}>
                  {!g.enPedido
                    ? <>El OP tiene {g.label.toLowerCase()} pero el pedido NO tiene el renglón "{g.prodNombre}"</>
                    : g.cantidadOk
                      ? <>El pedido tiene "{g.prodPedido?.desc || g.prodNombre}" con cantidad {g.cantPedido} ✓</>
                      : <>Cantidad distinta: OP suma <b>{g.cantDoc}</b> pero el pedido tiene <b>{g.cantPedido}</b></>}
                </div>
                {g.items.map((it, i) => (
                  <div key={i} style={{ ...rowStyle, color: "#64748b" }}>
                    {it.cant}× <b>{it.alias || it.codNum}</b> — {it.detalle}
                    {it.proveedor ? <span style={{ color: "#94a3b8" }}> · {it.proveedor}</span> : null}
                  </div>
                ))}
              </div>
            );
          })}

          {telas && (
            <>
              <div style={seccionTitulo("#b45309")}>
                📏 Tela mosquitera — se despacha desde Gualeguay
              </div>
              {telas.calculo.tipos.map((t, i) => (
                <div key={i} style={rowStyle}>
                  <b>{t.tipo}</b>{t.rolloDesc ? ` (rollo ${t.rolloDesc})` : ""}: {t.piezas} piezas
                  {t.rollos != null ? <> → <b>{t.rollos.toFixed(2)} rollos</b></> : " (sin medidas legibles)"}
                </div>
              ))}
              <div style={{ fontSize: 13, fontWeight: 700, color: "#b45309", padding: "6px 0" }}>
                Total ≈ {telas.calculo.rollosTotal.toFixed(2)} rollos
              </div>
              <div style={{ fontSize: 12, color: telas.enPedido ? (telas.cantidadOk ? "#15803d" : "#b91c1c") : "#b45309" }}>
                {!telas.enPedido ? (
                  <>⚠️ Vinculá el pedido de tela de Gualeguay a la obra (hoy la tela se usa sin descontar stock)</>
                ) : telas.cantidadOk ? (
                  <>✓ El pedido de tela{telas.origen === "pedidoTela" ? " (Gualeguay)" : ""} tiene "{telas.prodPedido.desc}"
                    con cantidad {telas.prodPedido.cant} — coincide con el cálculo</>
                ) : (
                  <>⚠️ El pedido de tela{telas.origen === "pedidoTela" ? " (Gualeguay)" : ""} tiene cantidad{" "}
                    <b>{telas.prodPedido.cant}</b> pero el cálculo da <b>{telas.calculo.rollosTotal.toFixed(2)}</b> rollos</>
                )}
              </div>
            </>
          )}

          {terceros.length > 0 && (
            <>
              <div style={seccionTitulo("#64748b")}>
                ℹ️ Anodizado / terceros — no pasan por el pedido ({terceros.length})
              </div>
              {terceros.map((it, i) => (
                <div key={i} style={{ ...rowStyle, color: "#94a3b8" }}>
                  {it.cant}× <b>{it.alias || it.codNum}</b> — {it.detalle}
                  {it.proveedor ? ` · ${it.proveedor}` : ""}
                </div>
              ))}
            </>
          )}

          <div style={seccionTitulo("#15803d")}>✅ Coinciden ({coinciden.length})</div>
          {coinciden.map((f, i) => (
            <div key={i} style={rowStyle}>
              <b>{f.clave}</b> — {f.descPedido || f.detalle} · cant. {f.cantDoc}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
